import Link from "next/link";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { WavyLines } from "./HeroBackground";
import { cn } from "@/lib/utils";

export function Container({ className, ...props }) {
  return <div className={cn("mx-auto w-full max-w-6xl px-6", className)} {...props} />;
}

export function Mark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="var(--brand)" />
      <path d="M9 23.5V13.5a3.5 3.5 0 0 1 7 0V23.5M16 13.5a3.5 3.5 0 0 1 7 0V23.5" fill="none" stroke="#ffffff" strokeWidth="4" />
    </svg>
  );
}

export function Eyebrow({ children, onDark = false, className }) {
  return (
    <p className={cn("text-sm font-medium", onDark ? "text-band-foreground/70" : "text-muted-foreground", className)}>
      {children}
    </p>
  );
}

// Text link whose underline grows on hover, with a diagonal arrow nudge.
export function ArrowLink({ href, children, className }) {
  return (
    <Link href={href} className={cn("group inline-flex items-center gap-1.5 font-semibold", className)}>
      <span className="bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_1px] bg-left-bottom bg-no-repeat pb-0.5 transition-[background-size] duration-300 group-hover:bg-[length:100%_1px]">
        {children}
      </span>
      <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-[3px] group-hover:-translate-y-[3px]" aria-hidden="true" />
    </Link>
  );
}

// Back/forward links between posts or case studies.
export function NavCard({ href, label, title, align = "left" }) {
  return (
    <Link
      href={href}
      className={cn(
        "group flex flex-col gap-1.5 rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25",
        align === "right" && "items-end text-right"
      )}
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-lg font-bold">{title}</span>
    </Link>
  );
}

// Top of every inner page: eyebrow, h1, lead, on a faint contour background.
export function PageHeader({ eyebrow, title, lead, children, className }) {
  return (
    <header className={cn("relative overflow-hidden pt-14 pb-12 md:pt-20 md:pb-16", className)}>
      <WavyLines count={12} className="absolute inset-0 size-full text-foreground [mask-image:radial-gradient(ellipse_70%_90%_at_85%_30%,#000_10%,transparent_70%)]" />
      <Container className="relative space-y-4">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className="max-w-3xl font-display text-5xl leading-[1.04] font-bold md:text-6xl">{title}</h1>
        {lead && <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground">{lead}</p>}
        {children}
      </Container>
    </header>
  );
}

// Closing call to action for inner pages; sends people to the homepage contact form.
export function CtaBand({ title = "Want a website that brings in customers?", body = "Tell me about your business and I’ll reply within one working day with how I’d approach it and a fixed price." }) {
  return (
    <section aria-label="Get in touch" className="pb-20 md:pb-24">
      <Container>
        <div className="relative overflow-hidden rounded-[2rem] bg-band px-6 py-12 text-band-foreground sm:px-8 md:rounded-[2.5rem] md:px-14 md:py-14">
          <WavyLines count={12} opacity={0.1} className="absolute inset-0 size-full text-band-foreground [mask-image:radial-gradient(ellipse_60%_90%_at_100%_50%,#000_10%,transparent_70%)]" />
          <div className="relative flex flex-wrap items-center justify-between gap-8">
            <div className="max-w-xl space-y-3">
              <h2 className="text-3xl font-bold md:text-4xl">{title}</h2>
              <p className="text-band-foreground/70">{body}</p>
            </div>
            <Link
              href="/#contact"
              className="group inline-flex h-13 items-center gap-2 rounded-full bg-cta px-7 font-semibold text-cta-foreground transition-all duration-300 hover:-translate-y-0.5 hover:shadow-cta"
            >
              Start your website
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-[3px]" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </Container>
    </section>
  );
}

// Links in Markdown bodies: other sites open in a new tab, pages on this site stay in it.
export function MarkdownLink({ node, href = "", ...props }) {
  const external = /^https?:\/\//.test(href);
  return <a href={href} {...(external && { target: "_blank", rel: "noopener noreferrer" })} {...props} />;
}
